import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { getApiErrorMessage } from "@/lib/api";
import { Logo } from "@/components/shared";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Star, CreditCard, User, Briefcase } from "lucide-react";

export default function Auth() {
    const [params] = useSearchParams();
    const { login, register } = useAuth();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
    const [role, setRole] = useState(params.get("role") === "professional" ? "professional" : "client");
    const [form, setForm] = useState({ name: "", email: "", password: "", city: "", phone: "" });
    const [loading, setLoading] = useState(false);

    const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (mode === "register") {
            if (!form.name.trim()) return toast.error(t("Inserisci il tuo nome."));
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return toast.error(t("Inserisci un indirizzo email valido."));
            if (form.password.length < 6) return toast.error(t("La password deve contenere almeno 6 caratteri."));
        }
        setLoading(true);
        try {
            let u;
            if (mode === "register") {
                u = await register({ ...form, role, lang: i18n.language.startsWith("en") ? "en" : "it" });
                toast.success(t("Benvenuto su Ciapum!"));
            } else {
                u = await login(form.email, form.password);
                toast.success(t("Bentornato!"));
            }
            const path = u.role === "admin" ? "/admin" : u.role === "professional" ? "/pro" : "/cliente";
            navigate(path);
        } catch (err) {
            console.error("Auth error:", err);
            toast.error(getApiErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-cream">
            {/* LEFT PANEL */}
            <div className="hidden lg:flex relative bg-ink text-white overflow-hidden flex-col justify-between p-12">
                <div className="grain" />
                <Logo light testid="auth-logo-desktop" />
                <div className="relative">
                    <h2 className="font-heading text-4xl font-black leading-tight">
                        {t("La fiducia è il nostro")}<br /><span className="text-terracotta">{t("mestiere.")}</span>
                    </h2>
                    <div className="mt-10 space-y-6">
                        {[
                            { icon: ShieldCheck, t: "Professionisti verificati", d: "Identità e competenze controllate." },
                            { icon: Star, t: "Recensioni reali", d: "Solo da lavori realmente completati." },
                            { icon: CreditCard, t: "Pagamenti protetti", d: "In garanzia fino a lavoro finito." },
                        ].map((f) => (
                            <div key={f.t} className="flex items-start gap-4">
                                <div className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                                    <f.icon className="h-5 w-5 text-terracotta" />
                                </div>
                                <div>
                                    <p className="font-heading font-bold">{t(f.t)}</p>
                                    <p className="text-sm text-white/60">{t(f.d)}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <p className="relative text-sm text-white/40">© {new Date().getFullYear()} Ciapum</p>
            </div>

            {/* FORM */}
            <div className="flex items-center justify-center p-6 sm:p-12">
                <div className="w-full max-w-md">
                    <div className="lg:hidden mb-8"><Logo testid="auth-logo-mobile" /></div>
                    <h1 className="font-heading text-3xl font-black text-ink">
                        {mode === "register" ? t("Crea il tuo account") : t("Bentornato")}
                    </h1>
                    <p className="mt-2 text-ink/60">
                        {mode === "register" ? t("Unisciti a Ciapum in pochi secondi.") : t("Accedi per continuare.")}
                    </p>

                    {mode === "register" && (
                        <div className="mt-6 grid grid-cols-2 gap-3">
                            {[
                                { v: "client", icon: User, t: "Sono un cliente", d: "Cerco un servizio" },
                                { v: "professional", icon: Briefcase, t: "Sono un pro", d: "Offro un servizio" },
                            ].map((r) => (
                                <button
                                    key={r.v}
                                    type="button"
                                    onClick={() => setRole(r.v)}
                                    className={`rounded-xl border p-4 text-left transition-colors ${
                                        role === r.v ? "border-terracotta bg-terracotta/5" : "border-border bg-white hover:border-ink/30"
                                    }`}
                                    data-testid={`role-${r.v}`}
                                >
                                    <r.icon className={`h-5 w-5 ${role === r.v ? "text-terracotta" : "text-ink/50"}`} />
                                    <p className="mt-2 font-semibold text-sm text-ink">{t(r.t)}</p>
                                    <p className="text-xs text-ink/50">{t(r.d)}</p>
                                </button>
                            ))}
                        </div>
                    )}

                    <form onSubmit={submit} noValidate className="mt-6 space-y-4">
                        {mode === "register" && (
                            <div>
                                <Label htmlFor="name">{t("Nome completo")}</Label>
                                <Input id="name" required value={form.name} onChange={upd("name")} className="mt-1.5 bg-white" data-testid="input-name" />
                            </div>
                        )}
                        <div>
                            <Label htmlFor="email">Email</Label>
                            <Input id="email" type="email" required value={form.email} onChange={upd("email")} className="mt-1.5 bg-white" data-testid="input-email" />
                        </div>
                        <div>
                            <Label htmlFor="password">Password</Label>
                            <Input id="password" type="password" required minLength={6} value={form.password} onChange={upd("password")} className="mt-1.5 bg-white" data-testid="input-password" />
                            {mode === "register" && <p className="mt-1 text-xs text-ink/50">{t("Almeno 6 caratteri.")}</p>}
                        </div>
                        {mode === "register" && (
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label htmlFor="city">{t("Città")}</Label>
                                    <Input id="city" value={form.city} onChange={upd("city")} className="mt-1.5 bg-white" data-testid="input-city" />
                                </div>
                                <div>
                                    <Label htmlFor="phone">{t("Telefono")}</Label>
                                    <Input id="phone" value={form.phone} onChange={upd("phone")} className="mt-1.5 bg-white" data-testid="input-phone" />
                                </div>
                            </div>
                        )}
                        <Button type="submit" disabled={loading} className="w-full h-12 bg-ink hover:bg-inkhover rounded-full text-base font-semibold" data-testid="submit-auth">
                            {loading ? t("Attendi…") : mode === "register" ? t("Registrati") : t("Accedi")}
                        </Button>
                    </form>

                    <p className="mt-6 text-center text-sm text-ink/60">
                        {mode === "register" ? t("Hai già un account?") : t("Non hai un account?")}{" "}
                        <button
                            onClick={() => setMode(mode === "register" ? "login" : "register")}
                            className="font-semibold text-terracotta hover:underline"
                            data-testid="toggle-mode"
                        >
                            {mode === "register" ? t("Accedi") : t("Registrati")}
                        </button>
                    </p>
                    <p className="mt-6 text-center"><Link to="/" className="text-xs text-ink/40 hover:text-ink">{t("← Torna alla home")}</Link></p>
                </div>
            </div>
        </div>
    );
}
